import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConversation } from '../../hooks/useConversation';
import ChatDrawerHeader from './ChatDrawerHeader';
import ChatDrawerContent from './ChatDrawerContent';
import GraphModal from './GraphModal';
import './ChatDrawer.css';

/**
 * ChatDrawer component - chat drawer for overview, projects and other pages
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls drawer open/closed state
 * @param {function} props.onToggle - Callback to toggle drawer
 * @param {string} props.context - Context identifier (e.g., 'overview', 'projects') for layout/customization
 * @param {Object} [props.conversation] - Optional conversation object (e.g. from useConversation() when used on overview/projects); falls back to shared context
 * @returns {JSX.Element} ChatDrawer component
 */
const ChatDrawer = ({ isOpen, onToggle, context, conversation: externalConversation }) => {
  // Use external conversation if provided (e.g. from Overview/Projects), otherwise use shared context
  const internalConversation = useConversation();
  const conversation = externalConversation || internalConversation;
  
  const {
    currentSessionId,
    sessions,
    messages,
    isLoading,
    isInitializing = false,
    sendQuestion,
    deleteSession,
    startNewSession,
    switchSession,
    handleMessageClick,
    selectedMessageId
  } = conversation;

  const [searchParams] = useSearchParams();
  const openedForSessionRef = useRef(null);
  useEffect(() => {
    const param = searchParams.get('session_id');
    if (!param || isOpen) return;
    if (openedForSessionRef.current === param) return;
    openedForSessionRef.current = param;
    onToggle();
  }, [searchParams, isOpen, onToggle]);

  const [selectedMessageForGraph, setSelectedMessageForGraph] = useState(null);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  
  // Resize functionality
  const defaultWidth = 400;
  const minWidth = 300;
  const maxWidth = 800;
  const storageKey = `chatDrawerWidth_${context || 'default'}`;
  
  const [drawerWidth, setDrawerWidth] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? parseInt(saved, 10) : defaultWidth;
  });
  
  const isResizing = useRef(false);
  const drawerRef = useRef(null);
  const toggleButtonRef = useRef(null);
  
  useEffect(() => {
    localStorage.setItem(storageKey, drawerWidth.toString());
  }, [drawerWidth, storageKey]);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current || !drawerRef.current) return;
    
    const container = drawerRef.current.parentElement;
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const newWidth = containerRect.right - e.clientX;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setDrawerWidth(newWidth);
      // Disable transition during resize for snappy feel
      if (drawerRef.current) {
        drawerRef.current.style.transition = 'none';
      }
      // Update button position immediately
      if (toggleButtonRef.current) {
        toggleButtonRef.current.style.transition = 'none';
        toggleButtonRef.current.style.right = `calc(${newWidth}px + 8px + var(--spacing-6))`;
      }
    }
  }, [minWidth, maxWidth]);
  
  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    // Re-enable transition after resize
    if (drawerRef.current) {
      drawerRef.current.style.transition = '';
    }
    // Re-enable transition for button
    if (toggleButtonRef.current) {
      toggleButtonRef.current.style.transition = '';
    }
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);
  
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleGraphClick = (messageId, stepIndex) => {
    const message = messages.find(msg => msg.id === messageId);
    const dataArray = message?.metadata?.data;
    const stepData = Array.isArray(dataArray) ? dataArray[stepIndex] : dataArray;
    if (message && stepData) {
      setSelectedMessageForGraph({ message, stepIndex });
      setIsGraphModalOpen(true);
    }
  };

  const handleCloseGraphModal = () => {
    setIsGraphModalOpen(false);
    setSelectedMessageForGraph(null);
  };


  return (
    <>
      <button
        ref={toggleButtonRef}
        className={`chat-drawer-toggle-left ${isOpen ? 'open' : 'closed'}`}
        onClick={onToggle}
        aria-label={isOpen ? 'Collapse chat' : 'Expand chat'}
        title={isOpen ? 'Collapse chat' : 'Expand chat'}
        style={isOpen ? { right: `calc(${drawerWidth}px + 8px + var(--spacing-6))` } : {}}
      >
        <svg 
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform var(--transition-base)'
          }}
        >
          <path 
            d="M7.5 9L4.5 6L7.5 3" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      <div 
        ref={drawerRef}
        className={`chat-drawer ${isOpen ? 'open' : 'closed'}`}
        style={isOpen ? { width: `${drawerWidth}px` } : {}}
      >
        {isOpen && (
          <div 
            className="chat-drawer-resize-handle"
            onMouseDown={handleMouseDown}
            role="separator"
            aria-label="Resize chat drawer"
            aria-orientation="vertical"
          />
        )}
        <ChatDrawerHeader
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSessionSelect={switchSession}
          onNewSession={startNewSession}
          onDeleteSession={deleteSession}
        />
        
        {isOpen && (
          <ChatDrawerContent
            messages={messages}
            isLoading={isLoading}
            isInitializing={isInitializing}
            onSendQuestion={sendQuestion}
            onMessageClick={handleMessageClick}
            selectedMessageId={selectedMessageId}
            onGraphClick={handleGraphClick}
            currentSessionId={currentSessionId}
          />
        )}
      </div>

      {isGraphModalOpen && selectedMessageForGraph && (
        <GraphModal
          isOpen={isGraphModalOpen}
          onClose={handleCloseGraphModal}
          message={selectedMessageForGraph.message}
          stepIndex={selectedMessageForGraph.stepIndex}
          messages={messages}
        />
      )}
    </>
  );
};

export default ChatDrawer;
